import React from "react";
import { useNavigate, useNavigationType } from "react-router";
import { ArrowBackIcon } from "../components/icons";
import useGoTo from "../hooks/useGoTo";

/**
 * Barra do topo da tela do produto. Sobre a foto ela é transparente, só com o
 * botão de voltar redondo; depois que a foto sai da tela, ganha fundo e o
 * nome do produto. Sem foto, já nasce sólida.
 *
 * O voltar segue o CommonHeader: chama `onBack` (que descarta o pedido em
 * andamento) e volta no histórico, ou vai para `fallbackPath` — a home, o
 * grupo de onde o produto veio ou a sacola, na edição de um item. Como o
 * useGoTo navega com replace, na prática é quase sempre o `fallbackPath`.
 */
export default function ProductTopBar({
  title,
  onBack,
  hasHero,
  fallbackPath = "/",
}) {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const goTo = useGoTo();
  const solid = useScrolledPast(hasHero ? HERO_SCROLL_THRESHOLD : 0);

  const handleBack = () => {
    onBack();
    if (navigationType === "PUSH") {
      navigate(-1);
    } else {
      goTo(fallbackPath);
    }
  };

  return (
    <div className={`product-topbar fixed-top${solid ? " is-solid" : ""}`}>
      <button
        type="button"
        className="icon-button product-topbar__back"
        onClick={handleBack}
        aria-label="Voltar"
      >
        <ArrowBackIcon />
      </button>
      <p className="product-topbar__title" aria-hidden={!solid}>
        {title}
      </p>
    </div>
  );
}

// A foto tem ~75% da largura de altura; a barra fica sólida um pouco antes
// dela sumir.
const HERO_SCROLL_THRESHOLD = 180;

function useScrolledPast(threshold) {
  const [past, setPast] = React.useState(
    () => threshold === 0 || window.pageYOffset > threshold,
  );

  // o estado inicial já veio do useState; aqui só acompanha a rolagem
  React.useEffect(() => {
    if (threshold === 0) return;
    const onScroll = () => setPast(window.pageYOffset > threshold);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return past;
}
